// One watcher per report. A generating Skipper Report can be watched two ways — the id-based
// status poll (reconciled from the reports list) and the generation job poll (started from a chat
// launch handle) — and they are created in different layers, so neither sees the other. Reopening
// the chart re-delivers the launch handle, which stacked a second watcher on a report the status
// poll was already tracking.
//
// The two are not interchangeable, so the winner is decided by rank rather than by who asked
// first: only the job poll carries the persisted report on its ticks and writes a terminal state,
// while the status poll answers lifecycle fields alone. Whoever holds a report is displaced when a
// higher-ranked watcher claims it, and refuses an equal or lower one. Without that, the order in
// which the two happen to start decides which one owns the row — and for a generation dispatched
// from the chat the list update that starts the status poll runs first every time, handing the row
// to the watcher that cannot finish it.
export const TRACKING_RANK = { status: 1, generation: 2 } as const

export type TrackingRank = (typeof TRACKING_RANK)[keyof typeof TRACKING_RANK]

interface Holder {
  rank: TrackingRank
  // called when a higher-ranked watcher displaces this one, so it stops rather than polls on
  stop?: () => void
}

const tracked = new Map<number, Holder>()

const trackable = (reportId?: number | null): reportId is number => Number.isInteger(reportId) && 0 < Number(reportId)

// True when the claim succeeded (the caller owns the report and must release it when it stops),
// false when a watcher of equal or higher rank already holds it — the caller must not start.
export const claimGuidedReportTracking = (
  reportId?: number | null,
  { rank = TRACKING_RANK.status, stop }: { rank?: TrackingRank; stop?: () => void } = {}
): boolean => {
  if (!trackable(reportId)) return false

  const holder = tracked.get(reportId)
  if (holder && holder.rank >= rank) return false
  // displace first: the loser's stop() releases the claim, so the new holder is written after it
  holder?.stop?.()

  tracked.set(reportId, { rank, stop })
  return true
}

export const releaseGuidedReportTracking = (reportId?: number | null): void => {
  if (trackable(reportId)) tracked.delete(reportId)
}

export const isGuidedReportTracked = (reportId?: number | null): boolean => trackable(reportId) && tracked.has(reportId)
